
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, MessageSquare } from "lucide-react";

interface BandDisagreementRecordsProps {
  disagreementsToShow: any[];
  showActions: boolean;
  isRecordsExpanded: boolean;
  setIsRecordsExpanded: (expanded: boolean) => void;
  isLoadingDisagreements: boolean;
  error: any;
  onActionClick: (record: any) => void;
  selectedRecord?: any;
  isSupervisor?: boolean;
}

const BandDisagreementRecords = ({
  disagreementsToShow,
  showActions,
  isRecordsExpanded,
  setIsRecordsExpanded,
  isLoadingDisagreements,
  error,
  onActionClick,
  selectedRecord,
  isSupervisor = false
}: BandDisagreementRecordsProps) => {
  const getStatusBadgeColor = (status: string) => {
    const statusColors = {
      'submitted': 'bg-purple-500/20 text-purple-300 border-purple-500/50',
      'under_review': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
      'resolved': 'bg-green-500/20 text-green-300 border-green-500/50',
      'escalated': 'bg-orange-500/20 text-orange-300 border-orange-500/50',
      'rejected': 'bg-red-500/20 text-red-300 border-red-500/50',
      'Band disagreement resolution completed': 'bg-blue-500/20 text-blue-300 border-blue-500/50'
    };
    return statusColors[status as keyof typeof statusColors] || statusColors.submitted;
  };

  const getBandBadgeColor = (band: string) => {
    const bandColors = {
      'A': 'bg-blue-500 text-white',
      'B': 'bg-green-500 text-white',
      'C': 'bg-yellow-500 text-black',
      'D': 'bg-red-500 text-white'
    };
    return bandColors[band as keyof typeof bandColors] || 'bg-gray-500 text-white';
  };

  // Filter disagreements based on privacy requirements
  const filteredDisagreements = selectedRecord 
    ? disagreementsToShow.filter(d => d.id === selectedRecord.id)
    : disagreementsToShow;

  // For non-supervisors, don't show collapsible header
  if (!isSupervisor) {
    return (
      <Card className="bg-secondary/50 border-2 border-primary">
        <CardHeader className="pb-3">
          <CardTitle className="text-white">Band Disagreement Requests</CardTitle>
        </CardHeader>
        
        <CardContent>
          {isLoadingDisagreements ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-primary animate-pulse">Loading band disagreements...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-red-400">Error loading band disagreements</div>
            </div>
          ) : filteredDisagreements.length === 0 ? (
            <div className="border border-gray-500/30 rounded-lg p-4 text-center">
              <p className="text-white text-sm">No band disagreements found.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="overflow-auto max-h-[600px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-secondary z-10">
                    <TableRow className="border-primary/20 hover:bg-secondary/30">
                      <TableHead className="text-primary text-[10px] w-[8%]">SN</TableHead>
                      <TableHead className="text-primary text-[10px] w-[20%]">Employee Name</TableHead>
                      <TableHead className="text-primary text-[10px] w-[15%]">Final Band</TableHead>
                      <TableHead className="text-primary text-[10px] w-[15%]">Requested Band</TableHead>
                      <TableHead className="text-primary text-[10px] w-[15%]">Resolution Outcome</TableHead>
                      <TableHead className="text-primary text-[8px] w-[17%]">Status</TableHead>
                      <TableHead className="text-primary text-[10px] w-[15%]">Submitted Date</TableHead>
                      <TableHead className="text-primary text-[10px] w-[5%]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDisagreements.map((disagreement: any, index: number) => (
                      <TableRow key={disagreement.id} className="border-primary/10 hover:bg-secondary/20">
                        <TableCell className="text-white text-[10px]">{index + 1}</TableCell>
                        <TableCell className="text-white text-[10px]">{disagreement.employee_name || disagreement.user_id}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-[10px] font-bold ${getBandBadgeColor(disagreement.final_band)}`}>
                            {disagreement.final_band}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-[10px] font-bold ${getBandBadgeColor(disagreement.requested_band)}`}>
                            {disagreement.requested_band}
                          </span>
                        </TableCell>
                        <TableCell>
                          {disagreement.resolution_outcome ? (
                            <span className={`px-2 py-1 rounded text-[10px] font-bold ${getBandBadgeColor(disagreement.resolution_outcome)}`}>
                              {disagreement.resolution_outcome}
                            </span>
                          ) : (
                            <span className="text-white text-[10px]">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusBadgeColor(disagreement.status)} text-[9.5px] whitespace-nowrap`}>
                            {disagreement.status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-white text-[10px]">
                          {new Date(disagreement.submitted_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onActionClick(disagreement)}
                            className="h-8 w-8 p-0 text-primary hover:bg-primary/20"
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // For supervisors, show collapsible header
  return (
    <Card className="bg-secondary/50 border-2 border-primary">
      <CardHeader 
        className="pb-3 cursor-pointer" 
        onClick={() => setIsRecordsExpanded(!isRecordsExpanded)}
      >
        <div className="flex justify-between items-center">
          <CardTitle className="text-white">Band Disagreement Requests</CardTitle>
          {isRecordsExpanded ? (
            <ChevronUp className="h-5 w-5 text-primary" />
          ) : (
            <ChevronDown className="h-5 w-5 text-primary" />
          )}
        </div>
      </CardHeader>
      
      {isRecordsExpanded && (
        <CardContent>
          {isLoadingDisagreements ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-primary animate-pulse">Loading band disagreements...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-red-400">Error loading band disagreements</div>
            </div>
          ) : filteredDisagreements.length === 0 ? (
            <div className="border border-gray-500/30 rounded-lg p-4 text-center">
              <p className="text-white text-sm">No band disagreements found.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="overflow-auto max-h-[600px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-secondary z-10">
                    <TableRow className="border-primary/20 hover:bg-secondary/30">
                      <TableHead className="text-primary text-[10px] w-[8%]">SN</TableHead>
                      <TableHead className="text-primary text-[10px] w-[20%]">Employee Name</TableHead>
                      <TableHead className="text-primary text-[10px] w-[15%]">Final Band</TableHead>
                      <TableHead className="text-primary text-[10px] w-[15%]">Requested Band</TableHead>
                      <TableHead className="text-primary text-[10px] w-[15%]">Resolution Outcome</TableHead>
                      <TableHead className="text-primary text-[8px] w-[17%]">Status</TableHead>
                      <TableHead className="text-primary text-[10px] w-[15%]">Submitted Date</TableHead>
                      {showActions && <TableHead className="text-primary text-[10px] w-[5%]">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDisagreements.map((disagreement: any, index: number) => (
                      <TableRow key={disagreement.id} className="border-primary/10 hover:bg-secondary/20">
                        <TableCell className="text-white text-[10px]">{index + 1}</TableCell>
                        <TableCell className="text-white text-[10px]">{disagreement.employee_name || disagreement.user_id}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-[10px] font-bold ${getBandBadgeColor(disagreement.final_band)}`}>
                            {disagreement.final_band}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-[10px] font-bold ${getBandBadgeColor(disagreement.requested_band)}`}>
                            {disagreement.requested_band}
                          </span>
                        </TableCell>
                        <TableCell>
                          {disagreement.resolution_outcome ? (
                            <span className={`px-2 py-1 rounded text-[10px] font-bold ${getBandBadgeColor(disagreement.resolution_outcome)}`}>
                              {disagreement.resolution_outcome}
                            </span>
                          ) : (
                            <span className="text-white text-[10px]">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusBadgeColor(disagreement.status)} text-[9.5px] whitespace-nowrap`}>
                            {disagreement.status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-white text-[10px]">
                          {new Date(disagreement.submitted_date).toLocaleDateString()}
                        </TableCell>
                        {showActions && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onActionClick(disagreement)}
                              className="h-8 w-8 p-0 text-primary hover:bg-primary/20"
                            >
                              <MessageSquare className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default BandDisagreementRecords;
